import React, { useEffect, useState } from "react";
import axios from "axios";
import { Modal, Button, Form } from "react-bootstrap";
import { roundMoneyUAH } from "../helpers/purchaseMoney";
import { MATERIAL_CATEGORY_OPTIONS, isPrintsEmbroideryCategory, MATERIAL_COLOR_NA_PLACEHOLDER } from "../helpers/materialCategories";
import { defaultMaterialFormUnitForCategory, isPackagingLikeMaterialUnit } from "../helpers/materialUnits";

function fabricLineQty(rollAmounts) {

    if (!Array.isArray(rollAmounts)) {
        return 0;
    }

    return rollAmounts.reduce((s, x) => s + Number(x || 0), 0);

}

export default function AddMaterialManualModal({
    show,
    onHide,
    onSaved,
    defaultExchangeRate,
    destinationWorkshopId = "",
}) {

    const [materialTypes, setMaterialTypes] = useState([]);
    const [materials, setMaterials] = useState([]);

    const [category, setCategory] = useState("FABRIC");
    const [currency, setCurrency] = useState("USD");
    const [exchangeRate, setExchangeRate] = useState(39);

    const [typeId, setTypeId] = useState(null);
    const [typeName, setTypeName] = useState("");
    const [color, setColor] = useState("");
    const [unit, setUnit] = useState("");
    const [rollAmounts, setRollAmounts] = useState([""]);
    const [qty, setQty] = useState("");
    const [priceUSD, setPriceUSD] = useState("");
    const [priceUAH, setPriceUAH] = useState("");
    const [note, setNote] = useState("");

    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {

        if (!show) {
            return;
        }

        (async () => {

            const [t, m] = await Promise.all([
                axios.get("/api/material-types"),
                axios.get("/api/materials")
            ]);

            setMaterialTypes(t.data);
            setMaterials(m.data);

        })();

    }, [show]);

    useEffect(() => {

        if (!show) {
            return;
        }

        const r =
            defaultExchangeRate != null &&
            Number.isFinite(Number(defaultExchangeRate)) &&
            Number(defaultExchangeRate) > 0
                ? Number(defaultExchangeRate)
                : null;

        setCategory("FABRIC");
        setCurrency("USD");
        setExchangeRate(r ?? 39);
        setTypeId(null);
        setTypeName("");
        setColor("");
        setUnit("");
        setRollAmounts([""]);
        setQty("");
        setPriceUSD("");
        setPriceUAH("");
        setNote("");
        setError("");
        setSaving(false);

    }, [show, defaultExchangeRate]);

    useEffect(() => {

        const loadRates = async () => {

            try {

                const cached = localStorage.getItem("usdRate");
                const cachedTime = localStorage.getItem("usdRateTime");
                const now = Date.now();

                if (cached && cachedTime && now - Number(cachedTime) < 3600000) {

                    const cr = Number(cached);

                    if (Number.isFinite(cr) && cr > 0) {
                        setExchangeRate(cr);
                    }

                    return;

                }

                const res = await fetch("/api/exchange-rates");
                const data = await res.json();

                if (!Array.isArray(data)) {
                    return;
                }

                const usd = data.find(
                    (c) => c.currencyCodeA === 840 && c.currencyCodeB === 980
                );

                if (usd) {

                    const rate = usd.rateSell || usd.rateCross;

                    setExchangeRate(rate);
                    localStorage.setItem("usdRate", String(rate));
                    localStorage.setItem("usdRateTime", String(now));

                }

            } catch (e) {

                console.error(e);

            }

        };

        if (show) {
            loadRates();
        }

    }, [show]);

    useEffect(() => {

        if (category === "FABRIC") {

            setRollAmounts((prev) =>
                Array.isArray(prev) && prev.length > 0 ? prev : [""]
            );

        }

    }, [category]);

    const filteredTypes = materialTypes.filter((t) => t.category === category);
    const typeRow = materialTypes.find((t) => t._id === typeId);

    const handleTypeInput = (value) => {

        setTypeName(value);

        const type = materialTypes.find((t) =>
            t.name.toLowerCase() === value.trim().toLowerCase()
        );

        if (type) {

            setTypeId(type._id);

            if (type.units?.length === 1) {
                const only = type.units[0];
                if (category === "FABRIC") {
                    setUnit(only);
                } else if (isPackagingLikeMaterialUnit(only)) {
                    setUnit(defaultMaterialFormUnitForCategory(category));
                } else {
                    setUnit(only);
                }
            }

        } else {

            setTypeId(null);

        }

    };

    const handleColorChange = (value) => {

        setColor(value);

        const material = materials.find((m) =>
            m.typeId?._id === typeId && m.color === value
        );

        if (material) {

            setPriceUSD(material.priceUSD ?? "");
            setPriceUAH(material.priceUAH ?? "");
            setUnit(material.unit || unit);

        }

    };

    const handleUsdChange = (value) => {

        setPriceUSD(value);
        const n = Number(value);

        if (Number.isFinite(n) && Number(exchangeRate) > 0) {
            setPriceUAH(n * Number(exchangeRate));
        }

    };

    const handleUahChange = (value) => {

        setPriceUAH(value);
        const n = Number(value);

        if (Number.isFinite(n) && Number(exchangeRate) > 0) {
            setPriceUSD(n / Number(exchangeRate));
        }

    };

    const updateRollAmount = (rollIndex, value) => {

        const rolls = [...rollAmounts];

        rolls[rollIndex] = value;
        setRollAmounts(rolls);

    };

    const addRoll = () => {
        setRollAmounts([...rollAmounts, ""]);
    };

    const removeRoll = (rollIndex) => {

        if (rollAmounts.length <= 1) {
            return;
        }

        const rolls = rollAmounts.filter((_, i) => i !== rollIndex);

        setRollAmounts(rolls);

    };

    const lineQty =
        category === "FABRIC" ? fabricLineQty(rollAmounts) : Number(qty || 0);

    const lineTotalUAH =
        lineQty * (Number.isFinite(Number(priceUAH)) ? Number(priceUAH) : 0);

    const resolvedUnit =
        category === "FABRIC" && typeRow?.units?.length === 1
            ? typeRow.units[0]
            : unit;

    const isValid = () => {

        if (!typeId && !typeName.trim()) {
            return false;
        }

        if (!isPrintsEmbroideryCategory(category) && !color.trim()) {
            return false;
        }

        const u = resolvedUnit != null ? String(resolvedUnit).trim() : "";

        if (
            !u &&
            !(category === "FABRIC" && typeRow?.units?.length === 1)
        ) {
            return false;
        }

        if (category === "FABRIC") {

            if (!Array.isArray(rollAmounts) || rollAmounts.length === 0) {
                return false;
            }

            for (const r of rollAmounts) {
                if (r === "" || r == null || Number(r) <= 0) {
                    return false;
                }
            }

        } else if (!qty || Number(qty) <= 0) {
            return false;
        }

        if (currency === "USD" && (!priceUSD || Number(priceUSD) <= 0)) {
            return false;
        }

        if (currency === "UAH" && (!priceUAH || Number(priceUAH) <= 0)) {
            return false;
        }

        return true;

    };

    const submit = async () => {

        setError("");
        setSaving(true);

        try {

            const payload = {
                category,
                currency,
                exchangeRate: Number(exchangeRate),
                typeId: typeId || null,
                typeName,
                color: isPrintsEmbroideryCategory(category)
                    ? MATERIAL_COLOR_NA_PLACEHOLDER
                    : color.trim(),
                unit: String(resolvedUnit || "").trim(),
                note: note.trim(),
                priceCurrency:
                    currency === "USD"
                        ? Number(priceUSD)
                        : Number(priceUAH),
                ...(category === "FABRIC"
                    ? { rollAmounts: rollAmounts.map((x) => Number(x)) }
                    : { qty: Number(qty) }),
                ...(destinationWorkshopId && String(destinationWorkshopId).trim() !== ""
                    ? { destinationWorkshopId: String(destinationWorkshopId).trim() }
                    : {}),
            };

            await axios.post("/api/materials/manual-add", payload, {
                withCredentials: true,
            });

            onHide();

            if (onSaved) {
                onSaved();
            }

        } catch (e) {

            const msg =
                e.response?.data?.message ||
                e.message ||
                "Не вдалося зберегти";

            setError(msg);

        } finally {

            setSaving(false);

        }

    };

    return (

        <Modal show={show} onHide={onHide} size="lg">

            <Modal.Header closeButton>

                <Modal.Title className="d-flex flex-column gap-1">
                    <span>Оприбуткування на склад без закупівлі</span>
                    <span className="text-muted fw-normal fs-6">
                        Без документа закупівлі та боргу перед постачальником
                    </span>
                </Modal.Title>

            </Modal.Header>

            <Modal.Body>

                <p className="text-muted small mb-3">
                    Для залишків «як є», перенесення з іншого обліку тощо: вкажіть тип, колір, кількість і
                    вартість за одиницю. У журналі руху це буде корекція, не закупівля.
                </p>

                {error && (
                    <div className="alert alert-danger py-2" role="alert">
                        {error}
                    </div>
                )}

                <Form.Group className="mb-3">

                    <Form.Label>Категорія</Form.Label>

                    <Form.Select
                        value={category}
                        onChange={(e) => {
                            const next = e.target.value;
                            setCategory(next);
                            if (next === "FABRIC") {
                                setUnit("");
                            } else {
                                setUnit(defaultMaterialFormUnitForCategory(next));
                            }
                        }}
                    >
                        {MATERIAL_CATEGORY_OPTIONS.map((o) => (
                            <option key={o.value} value={o.value}>
                                {o.label}
                            </option>
                        ))}
                    </Form.Select>
                    <Form.Text className="text-muted">
                        Категорія обмежує підказки типів; конкретну назву вводите в полі «Матеріал».
                    </Form.Text>

                </Form.Group>

                <div className="d-flex gap-3 mb-3 flex-wrap">

                    <Form.Group>

                        <Form.Label>Валюта ціни</Form.Label>

                        <Form.Select
                            value={currency}
                            onChange={(e) => setCurrency(e.target.value)}
                        >
                            <option value="USD">USD</option>
                            <option value="UAH">UAH</option>
                        </Form.Select>

                    </Form.Group>

                    <Form.Group>

                        <Form.Label>Курс (₴ за $)</Form.Label>

                        <Form.Control
                            type="number"
                            step="any"
                            value={exchangeRate}
                            onChange={(e) => setExchangeRate(e.target.value)}
                        />

                    </Form.Group>

                </div>

                <Form.Group className="mb-2">

                    <Form.Label>Матеріал</Form.Label>

                    <Form.Control
                        list="manual-add-types"
                        value={typeName}
                        onChange={(e) => handleTypeInput(e.target.value)}
                    />

                    <datalist id="manual-add-types">
                        {filteredTypes.map((t) => (
                            <option key={t._id} value={t.name} />
                        ))}
                    </datalist>

                </Form.Group>

                {!isPrintsEmbroideryCategory(category) ? (
                <Form.Group className="mb-2">

                    <Form.Label>Колір</Form.Label>

                    <Form.Control
                        list="manual-add-colors"
                        value={color}
                        onChange={(e) => handleColorChange(e.target.value)}
                    />

                    <datalist id="manual-add-colors">
                        {(typeRow?.colors || []).map((c) => (
                            <option key={c} value={c} />
                        ))}
                    </datalist>

                </Form.Group>
                ) : null}

                <Form.Group className="mb-3">

                    <Form.Label>Одиниця</Form.Label>

                    {category === "FABRIC" && typeRow?.units?.length === 1 && (
                        <div className="small text-muted mb-1">{typeRow.units[0]}</div>
                    )}

                    {category === "FABRIC" && typeRow?.units?.length > 1 && (
                        <Form.Select
                            value={unit || ""}
                            onChange={(e) => setUnit(e.target.value)}
                        >
                            <option value="">Оберіть од.</option>
                            {typeRow.units.map((u) => (
                                <option key={u} value={u}>
                                    {u}
                                </option>
                            ))}
                        </Form.Select>
                    )}

                    {(category !== "FABRIC" || !typeRow?.units?.length) && (
                        <Form.Control
                            value={unit}
                            onChange={(e) => setUnit(e.target.value)}
                            placeholder="кг / м / шт …"
                        />
                    )}

                </Form.Group>

                <Form.Group className="mb-3">

                    <Form.Label>
                        {category === "FABRIC" ? "Рулони (кількість по кожному)" : "Кількість"}
                    </Form.Label>

                    {category === "FABRIC" ? (
                        <div className="d-flex flex-column gap-2">
                            {rollAmounts.map((r, ri) => (
                                <div key={ri} className="d-flex align-items-center gap-2 flex-wrap">
                                    <span className="text-muted small">{ri + 1}.</span>
                                    <Form.Control
                                        type="number"
                                        step="any"
                                        style={{ maxWidth: 140 }}
                                        value={r}
                                        onChange={(e) =>
                                            updateRollAmount(ri, e.target.value)
                                        }
                                    />
                                    <span className="small text-muted">
                                        {resolvedUnit || "—"}
                                    </span>
                                    {rollAmounts.length > 1 && (
                                        <Button
                                            variant="outline-danger"
                                            size="sm"
                                            type="button"
                                            onClick={() => removeRoll(ri)}
                                        >
                                            ✕
                                        </Button>
                                    )}
                                </div>
                            ))}
                            <Button
                                variant="outline-secondary"
                                size="sm"
                                type="button"
                                className="align-self-start"
                                onClick={addRoll}
                            >
                                + Рулон
                            </Button>
                            <div className="small text-muted">
                                Разом: {fabricLineQty(rollAmounts).toLocaleString()}{" "}
                                {resolvedUnit || ""}
                            </div>
                        </div>
                    ) : (
                        <Form.Control
                            type="number"
                            step="any"
                            value={qty}
                            onChange={(e) => setQty(e.target.value)}
                        />
                    )}

                </Form.Group>

                <div className="d-flex gap-3 mb-3 flex-wrap">

                    <Form.Group className="flex-grow-1" style={{ minWidth: 140 }}>

                        <Form.Label>Ціна, $ / од.</Form.Label>

                        <Form.Control
                            type="number"
                            step="any"
                            value={priceUSD}
                            onChange={(e) => handleUsdChange(e.target.value)}
                        />

                    </Form.Group>

                    <Form.Group className="flex-grow-1" style={{ minWidth: 140 }}>

                        <Form.Label>Ціна, ₴ / од.</Form.Label>

                        <Form.Control
                            type="number"
                            step="any"
                            value={priceUAH}
                            onChange={(e) => handleUahChange(e.target.value)}
                        />

                    </Form.Group>

                </div>

                <Form.Group className="mb-2">

                    <Form.Label>Коментар (необов’язково)</Form.Label>

                    <Form.Control
                        as="textarea"
                        rows={2}
                        value={note}
                        onChange={(e) => setNote(e.target.value)}
                        placeholder="Звідки / для чого …"
                    />

                </Form.Group>

                <div className="small text-muted">
                    Орієнтовна сума рядка:{" "}
                    {roundMoneyUAH(lineTotalUAH).toLocaleString()} грн
                </div>

            </Modal.Body>

            <Modal.Footer>

                <Button variant="secondary" onClick={onHide} disabled={saving}>
                    Скасувати
                </Button>

                <Button onClick={submit} disabled={!isValid() || saving}>
                    {saving ? "Збереження…" : "Зберегти"}
                </Button>

            </Modal.Footer>

        </Modal>

    );

}
